import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import StaticWeChatPayModal from '../Payment/StaticWeChatPayModal'
import { RsIcon } from '../icons'
import { SUCCESS_FEE_OPTIONS, successFeeSummaryLines } from '../../constants/successFees'

function fmt(n, currency = '¥') {
  return `${currency}${Number(n).toLocaleString()}`
}

function parseGbFromText(text) {
  const m = String(text || '').match(/(\d+)\s*GB/i)
  return m ? Number(m[1]) : null
}

function SuccessFeeSection({ itemNumber }) {
  return (
    <div className="upgrade-pricing-section">
      <div className="upgrade-section-title">
        Item {itemNumber}：Success Fee
        <span className="upgrade-badge required">Required</span>
      </div>
      <div className="upgrade-options-grid">
        {SUCCESS_FEE_OPTIONS.map((fee) => (
          <div key={fee.id} className="upgrade-option-item selected static">
            <div className="upgrade-option-header">
              <div className="upgrade-option-icon green"><RsIcon name="dollarSign" size="lg" /></div>
              <div className="upgrade-option-name">{fee.name}</div>
            </div>
            <div className="upgrade-option-desc">
              {fee.percent}% of {fee.basis}, {fee.condition}.
            </div>
            <div className="upgrade-option-price">
              {fee.percent}%<span> × {fee.basisShort}</span>
            </div>
            <div className="upgrade-option-sub">No deal, no fee — auto-deducted when applicable</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function UpgradePricingBuilder({ role = 'GP', onCheckout, onPaid }) {
  const isGP = role === 'GP'
  const isStartup = role === 'Startup'
  const isLP = role === 'LP'
  const [catalog, setCatalog] = useState(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [period, setPeriod] = useState(3)
  const [storagePeriod, setStoragePeriod] = useState(3)
  const [selectedTier, setSelectedTier] = useState(null)
  const [startupTier, setStartupTier] = useState(null)
  const [domesticDisplay, setDomesticDisplay] = useState(false)
  const [emailCampaign, setEmailCampaign] = useState(false)
  const [roadshow, setRoadshow] = useState(null)
  const [summit, setSummit] = useState(null)
  const [storage, setStorage] = useState(null)
  const [lpRecurring, setLpRecurring] = useState(null)
  const [loading, setLoading] = useState(false)
  const [payOrder, setPayOrder] = useState(null)

  useEffect(() => {
    let cancelled = false
    setCatalogLoading(true)
    axios
      .get('/api/pricing/catalog', { params: { role } })
      .then((res) => {
        if (cancelled) return
        const cat = res.data?.catalog || null
        setCatalog(cat)
        if (cat?.tiers?.length) {
          setSelectedTier(cat.tiers[0])
          setStartupTier(cat.tiers[0])
        }
        if (cat?.periods?.length) setPeriod(cat.periods[0])
        if (cat?.storagePeriods?.length) setStoragePeriod(cat.storagePeriods[0])
      })
      .catch(() => toast.error('Failed to load pricing'))
      .finally(() => {
        if (!cancelled) setCatalogLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [role])

  const periods = catalog?.periods || [3, 6, 12]
  const storagePeriods = catalog?.storagePeriods || [3, 6, 12]
  const gpTiers = catalog?.tiers || []
  const gpRoadshow = catalog?.roadshow || []
  const gpSummit = catalog?.summit || []
  const gpStorage = catalog?.storage || []
  const lpRecurringOptions = catalog?.recurring || []
  const startupTiers = catalog?.tiers || []
  const startupRoadshow = catalog?.roadshow || []
  const startupSummit = catalog?.summit || []
  const startupStorage = catalog?.storage || []
  const emailCampaignPrice = catalog?.emailCampaignPrice ?? 0
  const domesticDisplayPrice = catalog?.domesticDisplayPrice ?? 0
  const emailCampaignUnit = emailCampaignPrice > 0 ? Math.round(emailCampaignPrice / 4) : 0

  const totals = useMemo(() => {
    if (!selectedTier && !startupTier && !lpRecurring) {
      return { total: 0, lines: [] }
    }
    if (isStartup) {
      const base = (startupTier?.monthly || 0) * period
      const display = domesticDisplay ? domesticDisplayPrice : 0
      const road = roadshow?.price || 0
      const sum = summit?.price || 0
      const stor = storage ? storage.price * storagePeriod : 0
      const total = base + display + road + sum + stor
      return {
        total,
        lines: [
          { label: 'Base Fee', value: base },
          { label: 'Domestic Display', value: display },
          { label: 'Roadshow Service', value: road },
          { label: 'Summit Sponsorship', value: sum },
          { label: 'Cloud Storage', value: stor },
        ],
      }
    }
    if (isLP) {
      const recurring = lpRecurring ? lpRecurring.price * period : 0
      return {
        recurring,
        total: recurring,
        lines: [
          { label: 'Recurring Fee', value: recurring },
        ],
      }
    }
    const base = (selectedTier?.monthly || 0) * period
    const email = emailCampaign ? emailCampaignPrice : 0
    const road = roadshow?.price || 0
    const sum = summit?.price || 0
    const stor = storage ? storage.price * storagePeriod : 0
    const total = base + email + road + sum + stor
    return {
      base,
      email,
      road,
      sum,
      stor,
      total,
      lines: [
        { label: 'Base Service Fee', value: base },
        { label: 'Email Campaign', value: email },
        { label: 'Roadshow Service', value: road },
        { label: 'Summit Sponsorship', value: sum },
        { label: 'Cloud Storage', value: stor },
      ],
    }
  }, [isGP, isLP, isStartup, selectedTier, startupTier, period, domesticDisplay, emailCampaign, roadshow, summit, storage, storagePeriod, lpRecurring, emailCampaignPrice, domesticDisplayPrice, lpRecurringOptions])

  if (catalogLoading || !catalog) {
    return <div className="upgrade-pricing-card" style={{ padding: 32, textAlign: 'center' }}>Loading pricing…</div>
  }

  const currencyLabel = catalog.currency === 'USD' ? 'USD' : 'CNY'
  const formatTotal = (n) => (isLP ? `$${n.toLocaleString()}` : fmt(n, catalog.currency === 'USD' ? '$' : '¥'))

  const handleCheckout = async () => {
    const buildLineItems = () =>
      totals.lines
        .filter((line) => line.value > 0)
        .map((line) => {
          const isStorage = /storage/i.test(line.label)
          const isRecurring =
            /base|recurring|fee/i.test(line.label) && !/success/i.test(line.label)
          const periodMonths = isStorage ? storagePeriod : isRecurring ? period : null
          return {
            label: line.label,
            value: line.value,
            periodMonths,
            periodLabel: periodMonths ? `${periodMonths} months` : 'One-time',
          }
        })

    let tierLabel = null
    if (isLP && lpRecurring) tierLabel = lpRecurring.label || lpRecurring.name
    else if (isStartup && startupTier) tierLabel = startupTier.name || startupTier.label
    else if (isGP && selectedTier) tierLabel = selectedTier.name || selectedTier.label

    const lineItems = buildLineItems().map((line) => {
      if (!/storage/i.test(line.label)) return line
      const storageItem = storage || startupStorage?.find((s) => line.label.includes(s.name)) || gpStorage?.find((s) => line.label.includes(s.name))
      const storageGb = parseGbFromText(storageItem?.sub) || parseGbFromText(line.label)
      return { ...line, storageGb }
    })

    const packageSummary = {
      tierLabel,
      subscriptionPeriodMonths: period,
      storagePeriodMonths: storage ? storagePeriod : null,
      storageGb: storage ? parseGbFromText(storage.sub) : null,
      storageLabel: storage ? `${storage.name} — ${storage.sub}` : null,
      role,
      selections: lineItems.map((l) => `${l.label} (${l.periodLabel})`),
    }

    if (onCheckout) {
      onCheckout(totals.total, {
        lines: totals.lines,
        lineItems,
        currency: currencyLabel,
        role,
        periodMonths: period,
        storagePeriodMonths: storage ? storagePeriod : null,
        packageSummary,
      })
      return
    }
    if (isLP && !lpRecurring) {
      toast.error('Please select a membership tier')
      return
    }
    if (totals.total > 200000) {
      toast.error('Amount exceeds ¥200,000. Please use corporate bank transfer and contact RotationStreet support.', { duration: 8000 })
      return
    }
    if (totals.total <= 0) {
      toast.error('Please select at least one paid service')
      return
    }
    setLoading(true)
    try {
      const res = await axios.post('/api/payments/static-qr/create', {
        amountYuan: totals.total,
        currency: isLP ? 'USD' : 'CNY',
        plan: 'tier2',
        role,
        lineItems,
        periodMonths: period,
        storagePeriodMonths: storage ? storagePeriod : null,
        packageSummary,
      })
      setPayOrder(res.data)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create payment order')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="upgrade-pricing-card">
      {isLP && (
        <div className="upgrade-lp-notice">
          <div className="upgrade-lp-notice-inner">
            <span style={{ fontSize: 24 }}>ℹ️</span>
            <div>
              <div className="upgrade-lp-notice-title">LP Pricing Policy</div>
              <div className="upgrade-lp-notice-text">
                <strong>Upgrade plan required only when LP is an asset seller.</strong>
                <br />
                <span style={{ color: '#666' }}>
                  When LP acts as a buyer or fund investor, the platform is{' '}
                  <strong style={{ color: '#0f3d8a' }}>free to use</strong>.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {isStartup && (
        <div className="upgrade-startup-notice">
          <div className="upgrade-startup-notice-inner">
            <span style={{ fontSize: 24 }}>🚀</span>
            <div>
              <div className="upgrade-startup-notice-title">Startup Pricing Policy</div>
              <div className="upgrade-startup-notice-text">
                <strong>Special startup-friendly rates.</strong> Base fee scales with your company valuation.
                Success fee only applies when deals close — no deal, no fee.
              </div>
            </div>
          </div>
        </div>
      )}

      {isStartup ? (
        <>
          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Base Fee
              <span className="upgrade-badge required">Required</span>
            </div>
            <div className="upgrade-tier-grid startup-tiers">
              {startupTiers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`upgrade-tier-item${startupTier.id === t.id ? ' selected' : ''}`}
                  onClick={() => setStartupTier(t)}
                >
                  <div className="upgrade-tier-range">{t.range}</div>
                  <div className="upgrade-tier-price">
                    {fmt(t.monthly)}
                    <span>/mo</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="upgrade-period-selector">
              {periods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`upgrade-period-option${period === m ? ' selected' : ''}`}
                  onClick={() => setPeriod(m)}
                >
                  {m} months
                </button>
              ))}
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 1：Domestic Display Fee
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              <button
                type="button"
                className={`upgrade-option-item${domesticDisplay ? ' selected' : ''}`}
                onClick={() => setDomesticDisplay(!domesticDisplay)}
              >
                <div className="upgrade-option-header">
                  <div className="upgrade-option-icon blue"><RsIcon name="chinese" size="lg" /></div>
                  <div className="upgrade-option-name">Domestic Showcase</div>
                </div>
                <div className="upgrade-option-desc">Feature your startup on the China domestic marketplace</div>
                <div className="upgrade-option-price">
                  {fmt(domesticDisplayPrice)}<span>/year</span>
                </div>
              </button>
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 2：Roadshow Service
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              {startupRoadshow.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${roadshow?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setRoadshow(roadshow?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon gold"><RsIcon name="mic" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">
                    {fmt(o.price)}
                    <span>{o.sub}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 3：Summit Sponsorship
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              {startupSummit.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${summit?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setSummit(summit?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon purple"><RsIcon name="trophy" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">{fmt(o.price)}</div>
                </button>
              ))}
            </div>
          </div>

          <SuccessFeeSection itemNumber={4} />

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 5：Cloud Storage
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-storage-grid">
              {startupStorage.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${storage?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setStorage(storage?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon cyan"><RsIcon name="storage" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">
                    {fmt(o.price)}
                    <span>/mo</span>
                  </div>
                  <div className="upgrade-option-sub">{o.sub}</div>
                </button>
              ))}
            </div>
            <div className="upgrade-period-selector">
              {storagePeriods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`upgrade-period-option${storagePeriod === m ? ' selected' : ''}`}
                  onClick={() => setStoragePeriod(m)}
                >
                  {m} months
                </button>
              ))}
            </div>
          </div>
        </>
      ) : isGP ? (
        <>
          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 1：Base Service Fee
              <span className="upgrade-badge required">Required</span>
            </div>
            <div className="upgrade-tier-grid">
              {gpTiers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`upgrade-tier-item${selectedTier.id === t.id ? ' selected' : ''}`}
                  onClick={() => setSelectedTier(t)}
                >
                  <div className="upgrade-tier-range">{t.range}</div>
                  <div className="upgrade-tier-price">
                    {fmt(t.monthly)}
                    <span>/mo</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="upgrade-period-selector">
              {periods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`upgrade-period-option${period === m ? ' selected' : ''}`}
                  onClick={() => setPeriod(m)}
                >
                  {m} months
                </button>
              ))}
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 2：Email Campaign
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              <button
                type="button"
                className={`upgrade-option-item${emailCampaign ? ' selected' : ''}`}
                onClick={() => setEmailCampaign(!emailCampaign)}
              >
                <div className="upgrade-option-header">
                  <div className="upgrade-option-icon blue"><RsIcon name="mail" size="lg" /></div>
                  <div className="upgrade-option-name">Email Campaign</div>
                </div>
                <div className="upgrade-option-price">
                  {fmt(emailCampaignUnit)}<span>/time × 4 = {fmt(emailCampaignPrice)}</span>
                </div>
              </button>
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 3：Roadshow Service
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              {gpRoadshow.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${roadshow?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setRoadshow(roadshow?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon gold"><RsIcon name="mic" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">
                    {fmt(o.price)}
                    <span>{o.sub}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 4：Summit Sponsorship
              <span className="upgrade-badge optional">Optional</span>
            </div>
            <div className="upgrade-options-grid">
              {gpSummit.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${summit?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setSummit(summit?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon purple"><RsIcon name="trophy" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">{fmt(o.price)}</div>
                </button>
              ))}
            </div>
          </div>

          <SuccessFeeSection itemNumber={5} />

          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 6：Cloud Storage
              <span className="upgrade-badge required">Required</span>
            </div>
            <div className="upgrade-storage-grid">
              {gpStorage.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${storage?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setStorage(storage?.id === o.id ? null : o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon cyan"><RsIcon name="storage" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">
                    {fmt(o.price)}
                    <span>/mo</span>
                  </div>
                  <div className="upgrade-option-sub">{o.sub}</div>
                </button>
              ))}
            </div>
            <div className="upgrade-period-selector">
              {storagePeriods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`upgrade-period-option${storagePeriod === m ? ' selected' : ''}`}
                  onClick={() => setStoragePeriod(m)}
                >
                  {m} months
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="upgrade-pricing-section">
            <div className="upgrade-section-title">
              Item 1：Recurring Fee
              <span className="upgrade-badge required">Required</span>
            </div>
            <div className="upgrade-options-grid">
              {lpRecurringOptions.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`upgrade-option-item${lpRecurring?.id === o.id ? ' selected' : ''}`}
                  onClick={() => setLpRecurring(o)}
                >
                  <div className="upgrade-option-header">
                    <div className="upgrade-option-icon blue"><RsIcon name="creditCard" size="lg" /></div>
                    <div className="upgrade-option-name">{o.name}</div>
                  </div>
                  <div className="upgrade-option-desc">{o.desc}</div>
                  <div className="upgrade-option-price">
                    ${o.price.toLocaleString()}
                    <span>/mo</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="upgrade-period-selector">
              {periods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`upgrade-period-option${period === m ? ' selected' : ''}`}
                  onClick={() => setPeriod(m)}
                >
                  {m} months
                </button>
              ))}
            </div>
          </div>

          <SuccessFeeSection itemNumber={2} />
        </>
      )}

      <div className="upgrade-summary-box">
        <div className="upgrade-summary-title">Summary ({currencyLabel})</div>
        {totals.lines.map((line) => (
          <div key={line.label} className="upgrade-summary-row">
            <span>{line.label}</span>
            <span>{formatTotal(line.value)}</span>
          </div>
        ))}
        {(isGP || isStartup || isLP) && successFeeSummaryLines().map((line) => (
          <div key={line.label} className="upgrade-summary-row muted">
            <span>{line.label}</span>
            <span>{line.value}</span>
          </div>
        ))}
        <div className="upgrade-summary-row">
          <span>Total</span>
          <span>{formatTotal(totals.total)}</span>
        </div>
        <button
          type="button"
          className="upgrade-checkout-btn"
          onClick={handleCheckout}
          disabled={loading || (isLP && !lpRecurring)}
        >
          {loading ? 'Processing…' : 'Proceed to Pay →'}
        </button>
      </div>

      {payOrder && (
        <StaticWeChatPayModal
          order={payOrder}
          onClose={() => setPayOrder(null)}
          onSuccess={() => {
            setPayOrder(null)
          }}
        />
      )}
    </div>
  )
}
